import {
    Box,
    FormControlLabel,
    Radio,
    RadioGroup as BaseRadioGroup,
    styled,
} from '@mui/material';
import type {
    RadioGroupProps as BaseRadioGroupProps,
    SxProps,
} from '@mui/material';
import { tss } from 'tss-react/mui';
import { forwardRef } from 'react';
import type { ComponentProps, ReactElement, ReactNode } from 'react';

const StyledRadio = styled(Radio)({
    alignSelf: 'baseline',
    padding: 0,
    marginRight: 9,
});
const StyledFormControlLabel = styled(FormControlLabel)<{
    direction: 'row' | 'column';
}>(({ direction }) => ({
    marginBottom: direction === 'column' ? 14 : 0,
    marginLeft: direction === 'row' ? 16 : 0,
    alignItems: direction === 'row' ? 'flex-start' : 'center',

    '&:first-of-type': {
        // Not sure why auto?
        marginLeft: direction === 'column' ? 0 : 'auto',
    },
    '&:last-of-type': {
        marginBottom: 0,
    },
}));
const RadioLabelContent = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    fontSize: 18,
    lineHeight: '24px',
    color: theme.palette.text.primary,
}));
const useRadioGroupStyles = tss
    .withParams<{ direction: 'row' | 'column' }>()
    .create(({ direction }) => ({
        root: {
            display: 'flex',
            flexDirection: direction,
        },
    }));

export type RadioGroupProps = Pick<
    BaseRadioGroupProps,
    'onChange' | 'value'
> & {
    sx?: SxProps;
    name?: string;
    items: ReadonlyArray<{
        value: string | number;
        label: ReactNode;
        description?: ReactNode;
        button?: ReactElement;
    }>;
    direction?: 'row' | 'column';
    className?: string;
    radioProps?: ComponentProps<typeof Radio>;
};

const RadioGroup = forwardRef(
    (
        {
            value,
            name,
            onChange,
            items,
            direction = 'column',
            className,
            radioProps,
            sx,
        }: RadioGroupProps,
        ref,
    ) => {
        const { classes, cx } = useRadioGroupStyles({ direction });
        return (
            <BaseRadioGroup
                sx={sx}
                aria-label={name}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
            >
                <div className={cx(classes.root, className)}>
                    {items.map(
                        ({
                            value: radioValue,
                            description,
                            label,
                            button,
                            ...otherProps
                        }) => (
                            <StyledFormControlLabel
                                direction={direction}
                                data-testid={`test-radio-button-${radioValue}`}
                                key={radioValue}
                                value={radioValue}
                                control={
                                    button ? (
                                        button
                                    ) : (
                                        <StyledRadio
                                            color="primary"
                                            data-testid={`test-radio-button-label-${radioValue}`}
                                            {...radioProps}
                                            {...otherProps}
                                        />
                                    )
                                }
                                label={
                                    <RadioLabelContent>
                                        <Box
                                            sx={{
                                                fontWeight:
                                                    radioValue === value
                                                        ? 'bold'
                                                        : 'normal',
                                            }}
                                        >
                                            {label}
                                        </Box>
                                        {description && (
                                            <Box color="text.secondary">
                                                {description}
                                            </Box>
                                        )}
                                    </RadioLabelContent>
                                }
                            />
                        ),
                    )}
                </div>
            </BaseRadioGroup>
        );
    },
);

export default RadioGroup;
